<template>
  <SuggestionsField
    v-model="query"
    :suggestions="suggestions"
    :field-component="fieldComponent"
    :field-props="fieldProps"
    :placeholder="placeholder"
    :errors="errors"
    :disabled="isDisabled"
    :filterable="false"
    :min-chars="minChars"
    :no-results-text="noResultsText"
    :loading="isLoading"
    :highlight-query="suggestionsQuery"
    select-first-on-enter
    preview-highlight
    @select="onSelect"
    @focus="onFocus"
    @blur="emit('blur')"
    @open-changed="emit('openChanged', $event)"
  >
    <template #footer>
      <!-- Required by Google Maps Platform policy when Places data is shown without a map. -->
      <div class="px-4 pt-1 text-right text-xs text-grey-500">
        Google Maps
      </div>
    </template>
  </SuggestionsField>
</template>

<script setup>
import debounce from 'lodash/debounce';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import SuggestionsField from '@/components/Ui/Forms/SuggestionsField.vue';
import { createPlacesAutocomplete, ensureMapsApiLoaded } from '@/services/places-autocomplete.js';
import { error } from '@/utils/log.js';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  /** Place fields to fetch once a suggestion is picked, e.g. `['viewport']`. */
  placeFields: {
    type: Array,
    required: true,
  },
  /** CLDR region codes the predictions are restricted to, e.g. `['gb']`. */
  regionCodes: {
    type: Array,
    default: () => [],
  },
  /** Place types the predictions are restricted to (max 5); all types when empty. */
  includedPrimaryTypes: {
    type: Array,
    default: () => [],
  },
  /** Field component rendered as the input; `SuggestionsField` falls back to `TextField`. */
  fieldComponent: {
    type: [Object, Function],
    default: undefined,
  },
  fieldProps: {
    type: Object,
    default: () => ({}),
  },
  placeholder: {
    type: String,
    default: '',
  },
  errors: {
    type: Array,
    default: () => [],
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  minChars: {
    type: Number,
    default: 1,
  },
  debounceMs: {
    type: Number,
    default: 250,
  },
  noResultsText: {
    type: String,
    default: '',
  },
  /** Overrides `window.GOOGLE_MAPS_API_KEY` when this page has to load the Maps JS API itself. */
  apiKey: {
    type: String,
    default: null,
  },
  /** Fetch predictions for a pre-filled value as soon as the field gains focus, before any keystroke. */
  suggestOnFocus: {
    type: Boolean,
    default: true,
  },
  /** When the field gains focus while holding exactly this text, it is cleared instead of queried. */
  clearOnFocusValue: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'select', 'focus', 'blur', 'openChanged']);

// Google answers in a few hundred milliseconds, so a loader tied to the request would only
// ever flash. Wait until the pause is long enough that a silent field would look broken.
const LOADING_INDICATOR_DELAY_MS = 2000;

const autocomplete = createPlacesAutocomplete({
  includedPrimaryTypes: props.includedPrimaryTypes,
  includedRegionCodes: props.regionCodes,
});

const suggestions = ref([]);
const isLoading = ref(false);
// The input the visible suggestions were fetched for. They outlive it by a keystroke or two
// while the next request is in flight, and marking them against the live query in the
// meantime makes the highlight jump around.
const suggestionsQuery = ref('');
let loadingIndicatorTimer = null;

// Picking a suggestion writes its text back into the field; that write must not
// trigger another prediction request.
let textWrittenBySelection = null;
// A consumer may rewrite the field from the resolved place (e.g. a full formatted address). That
// rewrite is not typing either: a request for it would bill a session nobody asked for.
let isHandlingSelection = false;

const query = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

function stopLoadingIndicator() {
  clearTimeout(loadingIndicatorTimer);
  loadingIndicatorTimer = null;
  isLoading.value = false;
}

const requestSuggestions = debounce(async (input) => {
  // The input may have been cleared or replaced while this request was in flight
  // (clearSuggestions cancels the debounce, not a started fetch); a stale commit would
  // resurface suggestions for text that is no longer in the field.
  const isStale = () => String(props.modelValue ?? '').trim() !== String(input).trim();

  try {
    const result = await autocomplete.fetchSuggestions(input);
    // `null` means a newer keystroke superseded this request; its own call resolves the loader.
    if (result !== null && !isStale()) {
      suggestions.value = result;
      suggestionsQuery.value = input;
      stopLoadingIndicator();
    }
  } catch (cause) {
    if (!isStale()) {
      suggestions.value = [];
      suggestionsQuery.value = input;
      stopLoadingIndicator();
    }
    error('Google Places autocomplete request failed', cause);
  }
}, props.debounceMs);

function clearSuggestions() {
  requestSuggestions.cancel();
  suggestions.value = [];
  suggestionsQuery.value = '';
  stopLoadingIndicator();
}

// The predictions already on screen stay there until fresh ones land, the way the legacy
// Google widget behaved - collapsing the dropdown to a loader row on every keystroke made
// it jump under the cursor. The timer only matters when there is nothing to show at all.
function scheduleFetch(input) {
  loadingIndicatorTimer ??= setTimeout(() => {
    isLoading.value = true;
  }, LOADING_INDICATOR_DELAY_MS);
  requestSuggestions(input);
}

watch(() => props.modelValue, (value) => {
  if (isHandlingSelection) {
    return;
  }

  if (value === textWrittenBySelection) {
    textWrittenBySelection = null;
    return;
  }

  if (!value || value.trim().length < props.minChars) {
    clearSuggestions();
    return;
  }

  scheduleFetch(value);
});

watch(() => props.regionCodes, (regionCodes) => {
  autocomplete.setRegionCodes(regionCodes);
  autocomplete.reset();
  clearSuggestions();
}, { deep: true });

// The legacy widget showed predictions as soon as a pre-filled field was focused,
// so fetch immediately for the current value instead of waiting for a keystroke.
function onFocus() {
  emit('focus');

  if (props.clearOnFocusValue !== null && String(props.modelValue ?? '') === props.clearOnFocusValue) {
    query.value = '';
    return;
  }

  const value = String(props.modelValue ?? '').trim();
  if (props.suggestOnFocus && value.length >= props.minChars && !suggestions.value.length) {
    scheduleFetch(value);
    requestSuggestions.flush();
  }
}

async function onSelect(suggestion) {
  textWrittenBySelection = suggestion.value;
  clearSuggestions();

  try {
    const place = await autocomplete.resolvePlace(suggestion, props.placeFields);
    isHandlingSelection = true;
    emit('select', { place, suggestion });
    // The consumer's rewrite reaches the watcher above in the flush this waits for.
    await nextTick();
  } catch (cause) {
    // Leave the field un-confirmed so the consumer's validation surfaces the problem.
    error('Failed to fetch Google place details', cause);
  } finally {
    isHandlingSelection = false;
  }
}

onMounted(() => {
  try {
    ensureMapsApiLoaded({ apiKey: props.apiKey });
  } catch (cause) {
    error('Failed to load the Google Maps JS API for places autocomplete', cause);
  }
});

onBeforeUnmount(() => {
  requestSuggestions.cancel();
  clearTimeout(loadingIndicatorTimer);
  autocomplete.reset();
});
</script>
